import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Users, Award, TrendingUp, Target, Heart } from 'lucide-react';

const AboutUs = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards in all our services.',
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our priority, and we tailor solutions to your needs.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for perfection in every trademark and copyright registration.',
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We leverage cutting-edge technology to streamline your brand protection.',
    },
    {
      icon: Target,
      title: 'Precision',
      description: 'Meticulous attention to detail ensures flawless application submissions.',
    },
    {
      icon: Heart,
      title: 'Dedication',
      description: 'We are passionate about protecting your intellectual property.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
            About Fine Trademark
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom duration-700">
            Your trusted partner in protecting intellectual property across the United States
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-heading">Our Story</h2>
              <p className="body-regular">
                Founded with a mission to democratize access to legal brand protection services, Fine Trademark has grown to become one of the most trusted trademark registration agencies in the United States. With over 15 years of experience, we've helped more than 10,000 businesses secure their intellectual property rights.
              </p>
              <p className="body-regular">
                Our team of experienced attorneys and trademark specialists understands that your brand is more than just a name or logo—it's the heart of your business. We're committed to providing affordable, efficient, and comprehensive trademark registration services that protect what you've worked so hard to build.
              </p>
              <p className="body-regular">
                From startups to established enterprises, we've served clients across every industry, delivering personalized solutions and maintaining an industry-leading 98% approval rate. Our dedication to excellence and client satisfaction has made us the go-to choice for businesses seeking reliable intellectual property protection.
              </p>
            </div>
            <div className="relative h-96 lg:h-full min-h-[400px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800"
                alt="Legal Services"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 hover:border-[#E5B80B] transition-colors duration-300">
              <CardContent className="p-10">
                <div className="inline-block p-4 bg-[#E5B80B]/10 rounded-lg mb-6">
                  <Target className="h-12 w-12 text-[#E5B80B]" />
                </div>
                <h3 className="sub-heading mb-4">Our Mission</h3>
                <p className="body-regular">
                  To provide accessible, affordable, and exceptional trademark registration services that empower businesses to protect their brands and thrive in competitive markets. We believe every business deserves professional legal protection, regardless of size or budget.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#E5B80B] transition-colors duration-300">
              <CardContent className="p-10">
                <div className="inline-block p-4 bg-[#E5B80B]/10 rounded-lg mb-6">
                  <TrendingUp className="h-12 w-12 text-[#E5B80B]" />
                </div>
                <h3 className="sub-heading mb-4">Our Vision</h3>
                <p className="body-regular">
                  To be the leading trademark registration and intellectual property protection service in the United States, recognized for our commitment to excellence, innovation, and client success. We envision a future where every entrepreneur has the tools and support to protect their creative work.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Our Core Values</h2>
            <p className="body-large max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="inline-block p-4 bg-[#E5B80B]/10 rounded-full mb-6 group-hover:bg-[#E5B80B] transition-colors duration-300">
                      <Icon className="h-10 w-10 text-[#E5B80B] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="sub-heading mb-4">{value.title}</h3>
                    <p className="body-regular">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-[#E5B80B] mb-2">15+</div>
              <p className="text-xl">Years of Experience</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#E5B80B] mb-2">10,000+</div>
              <p className="text-xl">Trademarks Registered</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#E5B80B] mb-2">98%</div>
              <p className="text-xl">Success Rate</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#E5B80B] mb-2">50+</div>
              <p className="text-xl">Industries Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Why Businesses Choose Us</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h3 className="sub-heading">Expertise You Can Trust</h3>
              <p className="body-regular">
                Our team comprises seasoned attorneys and trademark specialists with decades of combined experience in intellectual property law. We stay updated on the latest USPTO regulations and procedures to ensure your applications meet all requirements and have the highest chance of approval.
              </p>
              <p className="body-regular">
                Every member of our team is committed to delivering exceptional service and maintaining the highest professional standards. We don't just process applications—we provide strategic guidance to help you build a strong, defensible trademark portfolio.
              </p>
            </div>
            <div className="space-y-6">
              <h3 className="sub-heading">Affordable Excellence</h3>
              <p className="body-regular">
                We believe that quality legal services shouldn't be prohibitively expensive. Our transparent pricing structure and special offers, like our $45 trademark registration, make professional brand protection accessible to businesses of all sizes. There are no hidden fees, and we provide clear estimates upfront.
              </p>
              <p className="body-regular">
                Beyond affordability, we deliver value through comprehensive services, personalized attention, and ongoing support. When you choose Fine Trademark, you're not just getting a service provider—you're gaining a long-term partner in protecting your intellectual property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-heading mb-6">Ready to Work With Us?</h2>
          <p className="body-large text-[#1E1E1E]/90 mb-8">
            Join thousands of satisfied clients who trust us to protect their brands. Start your trademark registration today.
          </p>
          <Link to="/contact-us">
            <Button size="lg" className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105">
              Register Your Trademark
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
